import { createSelector } from '@reduxjs/toolkit';

import {
  getLoggedInUser,
  hasTrussAnalysisInteractPermission,
  hasTrussPropertiesInteractPermission,
  isLoggedInUserEngineer,
} from '@paragon/login-tools';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { RootState } from '..';
import { getActiveAnalysisSet } from './getActiveAnalysisSetSelector';
import { getActiveComponentDesign } from './getActiveComponentDesignSelector';
import { getActiveStation } from './getActiveStation';
import { getAnalysisHistoryForActiveComponentDesign } from './getAnalysisHistoryForActiveComponentDesignSelector';
import { getProjectLayoutModel } from './getProjectLayoutModel';
import { isInsertingComponentDesign } from './isInsertingComponentDesignSelector';
import { getActiveJobLayoutModel } from './jobContextSelectors';
import { doWorkingChangesExistForActiveComponentDesign } from './workingChangesSelectors';

export const isViewingOldAnalysisSet = createSelector(
  (state: RootState) => getAnalysisHistoryForActiveComponentDesign(state),
  getActiveAnalysisSet,
  doWorkingChangesExistForActiveComponentDesign,
  (history, activeAnalysisSet, hasWorkingChanges) =>
    (activeAnalysisSet &&
      history &&
      history[0] &&
      (history[0].analysisSetGuid !== activeAnalysisSet.guid || hasWorkingChanges)) ||
    false,
);

export const isViewingOtherOrganizationsComponent = createSelector(
  [
    getLoggedInUser,
    (state: RootState) => state.application.openContext,
    getActiveComponentDesign,
    getProjectLayoutModel,
    getActiveJobLayoutModel,
    getActiveStation,
    (state) => state.sharedComponentSet.sharedComponentSet,
  ],

  (user, context, componentDesign, project, job, station, sharedComponentSet) => {
    // If you're not logged in we don't know if this component belongs to your organization or not
    if (!user) return true;

    switch (context?.type) {
      case 'project':
        return project && user.organization !== project.organization;

      case 'shared-component-set':
        return (
          sharedComponentSet &&
          (context.sharedComponentSetLookupCode !== sharedComponentSet.lookupCode ||
            user.organization !== sharedComponentSet.organization)
        );

      case 'job':
        return job && user.organization !== job.ownerOrganization;

      case 'station':
      case 'schedule-station':
        return station && user.organization !== station.organization;

      case undefined:
        return componentDesign && user.organization !== componentDesign.organization;

      default:
        assertExhaustiveSwitch(context);
    }
  },
);

export const isPermittedToEditThisComponentDueToJobSharing = createSelector(
  getLoggedInUser,
  isLoggedInUserEngineer,
  getActiveJobLayoutModel,

  (user, loggedInUserIsEngineer, job) =>
    !!loggedInUserIsEngineer &&
    !!job &&
    !!user &&
    job.sharedWithOrganizations.includes(user.organization) &&
    job.jobType === 'Repair',
);

export const isActiveComponentDesign = createSelector(getActiveComponentDesign, (design) => design != null);

export const isViewingNonCommittedComponent = createSelector(
  getActiveComponentDesign,
  (state: RootState) => state.componentDesigns.loadedState,
  (activeComponentDesign, componentDesignsLoadedState) => {
    if (!activeComponentDesign) return false;
    return !componentDesignsLoadedState[activeComponentDesign.guid].committed;
  },
);

export const inViewOnlyMode = createSelector(
  isViewingOldAnalysisSet,
  isViewingOtherOrganizationsComponent,
  isPermittedToEditThisComponentDueToJobSharing,
  isViewingNonCommittedComponent,
  (state: RootState) => state.application.openContext?.type === 'shared-component-set',
  (state: RootState) => state.application.openContext?.type === 'schedule-station',

  (
    viewingOldAnalysisSet,
    viewingOtherOrganizationsComponent,
    permittedToEditComponentDueToJobSharing,
    viewingNonCommittedComponent,
    viewingSharedComponentSet,
    viewingScheduleStation,
  ) =>
    viewingOldAnalysisSet ||
    (viewingOtherOrganizationsComponent && !permittedToEditComponentDueToJobSharing) ||
    // The inclusion of this flag means that careful attention needs to be paid when using this selector. The component
    // is view-only in the sense that you shouldn't be able to edit the members, bearings, etc. directly, but if you're
    // in profile insertion, you should be able to edit the profile. In some cases, this selector was used to block
    // things like context menus and editor handles, but that is too broad of a restriction, since context menus and
    // editor handles can be used to edit profiles.
    viewingNonCommittedComponent ||
    // Even if the component in the SharedComponentSet belongs to the organization of the logged in viewer, the user is
    // viewing a truss that corresponds to a truss on a TDD. They should not be able to alter it. If they want to alter
    // it, that should be done in the source project.
    viewingSharedComponentSet ||
    // Regular stations can make certain edits to components, like saving user dimensions. But schedule stations should
    // not be able to make any edits to components at all since they are viewing a component from an AnalysisSet and
    // any changes would be saved to the current ComponentDesign.
    viewingScheduleStation,
);

export const settingsShouldBeViewOnly = createSelector(
  [isInsertingComponentDesign, inViewOnlyMode, hasTrussAnalysisInteractPermission],
  (insertingComponentDesign, viewOnly, hasRelevantPermission) =>
    insertingComponentDesign ? !hasRelevantPermission : !hasRelevantPermission || viewOnly,
);

export const propertiesShouldBeViewOnly = createSelector(
  inViewOnlyMode,
  hasTrussPropertiesInteractPermission,
  (viewOnly, hasRelevantPermission) => !hasRelevantPermission || viewOnly,
);
